import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Lock, ScanFace } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { api } from '../lib/api';
import { PinPad } from '../components/PinPad';
import { biometricLabel, verifyBiometric } from '../lib/webauthn';

const PIN_LEN = 6;
const WEBAUTHN_KEY = 'archivio.webauthn';

/**
 * Gate di blocco LOCALE dell'app (§6.10). Se l'utente ha PIN o biometria
 * attivi e il blocco automatico non è "Mai", al boot (e al ritorno da background
 * oltre la soglia) mostra la lock screen PRIMA di renderizzare i dati.
 */
export function LockGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const sec = user?.security;
  const hasMethod = !!(sec && (sec.pin_set || sec.biometric_enabled));
  const autoLock = sec?.auto_lock_seconds ?? -1;

  const [locked, setLocked] = useState(false);
  const initialized = useRef(false);
  const hiddenAt = useRef<number | null>(null);

  // Blocco iniziale al boot, una sola volta dopo il caricamento del profilo.
  useEffect(() => {
    if (initialized.current || !sec) return;
    initialized.current = true;
    if (hasMethod && autoLock !== -1) setLocked(true);
  }, [sec, hasMethod, autoLock]);

  // Auto-lock al ritorno da background oltre la soglia.
  useEffect(() => {
    if (!hasMethod || autoLock === -1) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
      } else if (hiddenAt.current != null) {
        const elapsed = (Date.now() - hiddenAt.current) / 1000;
        if (elapsed >= autoLock) setLocked(true);
        hiddenAt.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [hasMethod, autoLock]);

  if (locked && hasMethod) {
    return <LockScreen security={sec!} onUnlock={() => setLocked(false)} />;
  }
  return <>{children}</>;
}

function LockScreen({
  security,
  onUnlock,
}: {
  security: NonNullable<ReturnType<typeof useAuth>['user']>['security'];
  onUnlock: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pinKey, setPinKey] = useState(0);
  const credJson = typeof localStorage !== 'undefined' ? localStorage.getItem(WEBAUTHN_KEY) : null;
  const canBiometric = security.biometric_enabled && !!credJson;

  const tryBiometric = useCallback(async () => {
    if (!credJson) return;
    setError(null);
    const ok = await verifyBiometric(credJson);
    if (ok) onUnlock();
    else setError('Sblocco biometrico non riuscito');
  }, [credJson, onUnlock]);

  // Tentativo biometrico automatico all'avvio della lock screen.
  useEffect(() => {
    if (canBiometric) void tryBiometric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPin = async (pin: string) => {
    try {
      const { ok } = await api.verifyPin(pin);
      if (ok) return onUnlock();
      setError('PIN errato');
      setPinKey((k) => k + 1);
    } catch {
      setError('Verifica non riuscita');
      setPinKey((k) => k + 1);
    }
  };

  return (
    <div className="lockscreen">
      <span className="lockscreen__icon">
        <Lock size={30} />
      </span>
      <div style={{ fontSize: 20, fontWeight: 700 }}>Archivio protetto</div>

      {security.pin_set ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: -8 }}>Inserisci il PIN per sbloccare</div>
          <PinPad key={pinKey} length={PIN_LEN} onComplete={onPin} error={error} />
          {canBiometric && (
            <button className="btn" onClick={tryBiometric}>
              <ScanFace size={18} /> Sblocca con {biometricLabel()}
            </button>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: -8 }}>{error ?? 'Sblocca con la biometria'}</div>
          <button className="btn btn--accent" onClick={tryBiometric}>
            <ScanFace size={18} /> Sblocca con {biometricLabel()}
          </button>
        </>
      )}
    </div>
  );
}
